import {Field, useFormikContext} from 'formik';

import {
    FormikRadioGroup,
    FormikSelect,
    FormikSwitch,
    FormikTextarea,
    FormikTextInput,
    PlatformFieldset,
} from '@flexinet/ui-components';
import {Application, ApplicationCategory, ApplicationType, BusinessDomain} from '../types.ts';
import {DeploymentAccountGroupsAutocomplete} from '../../devops/deployment-account-groups/inputs/DeploymentAccountGroupsAutocomplete.tsx';


type FormValues = Application & {
    provisionAutomatically?: boolean,
};


export const FormComponent = () => {

    const {values} = useFormikContext<FormValues>();

    return (
        <>
            <PlatformFieldset legend={'Application Details'}>
                <Field
                    name='name'
                    label='Name'
                    description={values.provisionAutomatically
                        ? 'Lowercase letters, digits, and hyphens only; must start with a letter. Used to derive repo, pipeline, and subdomain names — "-app" and "-app-infra" are appended for you, so do not end the name in "-app".'
                        : 'Please enter the application name'}
                    component={FormikTextInput}
                    withAsterisk
                    my={'xl'}
                />

                <Field
                    name='description'
                    label='Description'
                    description='Please enter application description'
                    component={FormikTextarea}
                    my={'xl'}
                />

                <Field
                    name='applicationCategory'
                    label='Category'
                    description='Please select an application category'
                    component={FormikSelect}
                    data={[
                        {
                            label: ApplicationCategory.ADMIN,
                            value: ApplicationCategory.ADMIN
                        },
                        {
                            label: ApplicationCategory.BUSINESS,
                            value: ApplicationCategory.BUSINESS
                        },
                        {
                            label: ApplicationCategory.CONSUMER,
                            value: ApplicationCategory.CONSUMER
                        },
                    ]}
                    withAsterisk
                    my={'xl'}
                />

                <Field
                    name='applicationStatus'
                    label='Status'
                    description='Please enter application status'
                    component={FormikRadioGroup}
                    data={[
                        {
                            label: 'Active',
                            value: 'ACTIVE'
                        },
                        {
                            label: 'Inactive',
                            value: 'INACTIVE'
                        },
                    ]}
                    withAsterisk
                    my={'xl'}
                />

                <Field
                    name='url'
                    label='URL'
                    description='Please enter application url'
                    component={FormikTextInput}
                    my={'xl'}
                />

                <Field
                    name='email'
                    label='Email'
                    description='Please enter application email'
                    component={FormikTextInput}
                    my={'xl'}
                />

                <Field
                    name='phone'
                    label='Phone'
                    description='Please enter application phone number'
                    component={FormikTextInput}
                    my={'xl'}
                />

                <Field
                    name='logoUrl'
                    label='Logo URL'
                    description='Please enter logo url'
                    component={FormikTextInput}
                    my={'xl'}
                />

            </PlatformFieldset>

            <PlatformFieldset legend={'Programmatic Provisioning'}>
                <Field
                    name='provisionAutomatically'
                    label='Provision automatically'
                    description='Provision the application end-to-end (repo, pipeline, DNS, certificate, hosting). Leave off to register an application that is already provisioned.'
                    component={FormikSwitch}
                    my={'xl'}
                />

                {values.provisionAutomatically ? (
                    <>
                        <Field
                            name='displayName'
                            label='Display Name'
                            // FNP-1026: this description promised a default that
                            // did not exist. Leaving the field blank failed the
                            // whole provisioning run — the playbook's inputSchema
                            // requires displayName — AFTER the Application record
                            // was already written, stranding it in CREATING.
                            // The fallback is now implemented server-side in
                            // execute-create-playbook.ts, so the promise is true.
                            description='Human-friendly name shown in the UI; defaults to Name when left empty'
                            component={FormikTextInput}
                            my={'xl'}
                        />

                        <Field
                            name='businessDomain'
                            label='Business Domain'
                            description='Which platform domain this application belongs to'
                            component={FormikSelect}
                            data={Object.values(BusinessDomain)
                                        .map((value) => ({label: value, value}))}
                            withAsterisk
                            my={'xl'}
                        />

                        <Field
                            name='applicationType'
                            label='Application Type'
                            description='Provisioning archetype'
                            component={FormikSelect}
                            data={Object.values(ApplicationType)
                                        .map((value) => ({label: value, value}))}
                            withAsterisk
                            my={'xl'}
                        />

                        <Field
                            name='parentDomainName'
                            label='Parent Domain'
                            description='Hosted zone the application subdomain lives under'
                            component={FormikTextInput}
                            placeholder='flexi.network'
                            my={'xl'}
                        />

                        <Field
                            name='subdomain'
                            label='Subdomain'
                            description='Optional leftmost label of the application hostname (e.g. "admin" → admin.dev.flexi.photo / admin.flexi.photo). Leave blank to deploy at the apex of the parent domain.'
                            component={FormikTextInput}
                            placeholder='admin'
                            my={'xl'}
                        />

                        <Field
                            name='deploymentAccountGroupId'
                            label='Deployment Account Group'
                            description='Which AWS account group hosts the deployed application'
                            component={DeploymentAccountGroupsAutocomplete}
                            my={'xl'}
                        />
                    </>
                ) : null}
            </PlatformFieldset>
        </>
    );
};
